import { type Item, type CurriculumNode, MVP_NODES } from "./item-library.js";
import { type CardStore, type MasteryThresholds, MASTERY_PRESETS, nodeMastery } from "./scheduler.js";

export interface InstrumentRung {
  /** Matches the ExerciseSkillId for this rung, e.g. "fret-intervals". */
  id: string;
  label: string;
  nodeIds: readonly string[];
}

export interface RungMastery {
  rung: InstrumentRung;
  mastered: number;
  practicing: number;
  introduced: number;
  total: number;
}

export const GUITAR_RUNGS: readonly InstrumentRung[] = [
  { id: "fret-intervals",       label: "Interval Shapes",  nodeIds: ["fret-octave-shapes", "fret-wide-octave-shapes", "fret-skip1-intervals", "fret-intervals-standard", "fret-intervals-gb-offset"] },
  { id: "fret-scale-degrees",   label: "Scale Degrees",    nodeIds: ["fret-scale-degrees-major", "fret-scale-degrees-minor"] },
  { id: "fret-triads",          label: "Triad Shapes",     nodeIds: ["fret-triad-shapes"] },
  { id: "fret-inversions",      label: "Triad Inversions", nodeIds: ["fret-triad-inversions"] },
  { id: "fret-caged",           label: "CAGED System",     nodeIds: ["fret-caged-shapes-ea", "fret-caged-shapes-cgd"] },
  { id: "fret-caged-anchoring", label: "CAGED Anchoring",  nodeIds: ["fret-caged-anchoring"] },
  { id: "fret-scale-degrees-caged", label: "Degrees in Shapes", nodeIds: ["fret-scale-degrees-caged"] },
  { id: "fret-diatonic",        label: "Diatonic — Major", nodeIds: ["fret-diatonic-triads"] },
  { id: "fret-diatonic-minor",  label: "Diatonic — Minor", nodeIds: ["fret-diatonic-triads-minor"] },
  { id: "fret-seventh",         label: "Seventh Chords",   nodeIds: ["fret-seventh-shapes"] },
];

export function computeRungMastery(
  rung: InstrumentRung,
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
  nodes: CurriculumNode[] = MVP_NODES,
): RungMastery {
  let mastered = 0;
  let practicing = 0;
  let introduced = 0;
  let total = 0;
  for (const nodeId of rung.nodeIds) {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) continue;
    const m = nodeMastery(node, items, cards, thresholds);
    mastered += m.masteredCount;
    practicing += m.practicingCount;
    introduced += m.introducedCount;
    total += m.totalCount;
  }
  return { rung, mastered, practicing, introduced, total };
}

export function computeInstrumentRungs(
  rungs: readonly InstrumentRung[],
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
  nodes: CurriculumNode[] = MVP_NODES,
): RungMastery[] {
  return rungs.map((rung) => computeRungMastery(rung, items, cards, thresholds, nodes));
}
